import { useState, useEffect } from "react";
import api from "../../utils/api";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { uploadImageFile, uploadImageFiles, getImageUrl } from "../../utils/imageUtils";
import { INP, S, THEME, ImageUploadField, GalleryUploadBtn, useIsMobile } from "./AdminComponents";
import { useLanguage } from "../../context/LanguageContext";

const { G, G3, O, O2 } = THEME;

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link"],
    ["clean"],
  ],
};
const QUILL_FORMATS = ["header", "bold", "italic", "underline", "list", "bullet", "blockquote", "link"];

const EMPTY_FORM = { title: "", description: "", date: "", organizer: "", category: "", attendees: "", status: "चलिरहेको", featured: false, imageUrl: "", gallery: [] };

const STATUS_LABELS = {
  ne: { current: "चलिरहेको", upcoming: "आगामी", past: "सम्पन्न" },
  en: { current: "Ongoing", upcoming: "Upcoming", past: "Past" },
};
const STATUS_KEYS = ["current", "upcoming", "past"];

const CONTENT = {
  ne: {
    statTotal:          "कुल कार्यक्रम",
    statOngoing:        "चलिरहेको",
    statUpcoming:       "आगामी",
    statPast:           "सम्पन्न",
    deltaSoFar:         "अहिलेसम्म",
    deltaNow:           "हाल",
    deltaSchedule:      "तालिका",
    deltaDone:          "भइसकेको",
    searchPlaceholder:  "🔍 कार्यक्रम खोज्नुहोस्…",
    filterAllStatus:    "सबै",
    filterAllSector:    "सबै क्षेत्र",
    viewTable:          "☰ तालिका",
    viewGrid:           "⊞ ग्रिड",
    refresh:            "↻ ताजा",
    addBtn:             "+ कार्यक्रम थप्नुहोस्",
    colTitle:           "शीर्षक",
    colSector:          "क्षेत्र",
    colDate:            "मिति",
    colOrganizer:       "आयोजक",
    colAttendees:       "सहभागी",
    colStatus:          "स्थिति",
    colActions:         "कार्य",
    loading:            "कार्यक्रमहरू लोड हुँदैछ…",
    empty:              "कुनै कार्यक्रम भेटिएन।",
    edit:               "सम्पादन",
    delete:             "मेटाउनुहोस्",
    featuredTitle:      "विशेष",
    unfeatureTitle:     "विशेष हटाउनुहोस्",
    modalAdd:           "➕ कार्यक्रम थप्नुहोस्",
    modalEdit:          "✏️ कार्यक्रम सम्पादन गर्नुहोस्",
    lblTitle:           "शीर्षक *",
    titlePlaceholder:   "कार्यक्रमको शीर्षक",
    lblDate:            "मिति *",
    lblOrganizer:       "आयोजक *",
    organizerPlaceholder:"आयोजकको नाम (धेरै भए अल्पविरामले छुट्याउनुहोस्, जस्तै: संस्था क, संस्था ख)",
    lblAttendees:       "सहभागी",
    lblCategory:        "क्षेत्र *",
    lblStatus:          "स्थिति",
    lblDescription:     "विवरण *",
    descPlaceholder:    "कार्यक्रमको विवरण…",
    lblMainImage:       "मुख्य छवि",
    lblGallery:         (n) => `ग्यालेरी (${n} फोटो)`,
    galleryEmpty:       'अहिलेसम्म कुनै ग्यालेरी फोटो छैन — माथि "+ फोटो थप्नुहोस्" थिच्नुहोस्',
    lblFeatured:        "विशेष",
    featuredYes:        "हो — मुख्य पृष्ठमा पिन गरिएको",
    featuredNo:         "होइन",
    saveBtnAdd:         "💾 कार्यक्रम सिर्जना गर्नुहोस्",
    saveBtnEdit:        "💾 परिवर्तन सुरक्षित गर्नुहोस्",
    savingBtn:          "सुरक्षित हुँदैछ…",
    cancel:             "रद्द गर्नुहोस्",
    deleteTitle:        "कार्यक्रम मेटाउनुहोस्",
    deleteConfirm:      (title) => <>के तपाईं <strong>"{title}"</strong> मेटाउन निश्चित हुनुहुन्छ? यो कार्य पूर्वावस्थामा फर्काउन सकिँदैन।</>,
    deleteYes:          "हो, मेटाउनुहोस्",
    deleting:           "मेटाउँदैछ…",
    errLoad:            "कार्यक्रमहरू लोड गर्न असफल भयो।",
    errSave:            "सुरक्षित गर्न असफल भयो।",
    errDelete:          "मेटाउन असफल भयो।",
    errUpdate:          "अपडेट गर्न असफल भयो।",
    unknownError:       "अज्ञात त्रुटि",
    locale:             "ne-NP",
  },
  en: {
    statTotal:          "Total Events",
    statOngoing:        "Ongoing",
    statUpcoming:       "Upcoming",
    statPast:           "Past",
    deltaSoFar:         "So far",
    deltaNow:           "Now",
    deltaSchedule:      "Schedule",
    deltaDone:          "Completed",
    searchPlaceholder:  "🔍 Search events…",
    filterAllStatus:    "All",
    filterAllSector:    "All Sectors",
    viewTable:          "☰ Table",
    viewGrid:           "⊞ Grid",
    refresh:            "↻ Refresh",
    addBtn:             "+ Add Event",
    colTitle:           "Title",
    colSector:          "Sector",
    colDate:            "Date",
    colOrganizer:       "Organizer",
    colAttendees:       "Attendees",
    colStatus:          "Status",
    colActions:         "Actions",
    loading:            "Loading events…",
    empty:              "No events found.",
    edit:               "Edit",
    delete:             "Delete",
    featuredTitle:      "Featured",
    unfeatureTitle:     "Remove Featured",
    modalAdd:           "➕ Add Event",
    modalEdit:          "✏️ Edit Event",
    lblTitle:           "Title *",
    titlePlaceholder:   "Event title",
    lblDate:            "Date *",
    lblOrganizer:       "Organizer *",
    organizerPlaceholder:"Organizer name(s) — comma-separate if multiple, e.g. Org A, Org B",
    lblAttendees:       "Attendees",
    lblCategory:        "Sector *",
    lblStatus:          "Status",
    lblDescription:     "Description *",
    descPlaceholder:    "Event description…",
    lblMainImage:       "Main Image",
    lblGallery:         (n) => `Gallery (${n} photos)`,
    galleryEmpty:       'No gallery photos yet — click "+ Add Photo" above',
    lblFeatured:        "Featured",
    featuredYes:        "Yes — pinned on homepage",
    featuredNo:         "No",
    saveBtnAdd:         "💾 Create Event",
    saveBtnEdit:        "💾 Save Changes",
    savingBtn:          "Saving…",
    cancel:             "Cancel",
    deleteTitle:        "Delete Event",
    deleteConfirm:      (title) => <>Are you sure you want to delete <strong>"{title}"</strong>? This action cannot be undone.</>,
    deleteYes:          "Yes, Delete",
    deleting:           "Deleting…",
    errLoad:            "Failed to load events.",
    errSave:            "Failed to save.",
    errDelete:          "Failed to delete.",
    errUpdate:          "Failed to update.",
    unknownError:       "Unknown error",
    locale:             "en-US",
  },
};

function EventsPage() {
  const { language } = useLanguage();
  const t = CONTENT[language] || CONTENT.en;
  const statusLabels = STATUS_LABELS[language] || STATUS_LABELS.en;
  const isMobile = useIsMobile();

  const [sectors, setSectors]           = useState([]); // [{ _id, name, order, ... }] from Sectors API
  const [events, setEvents]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [search, setSearch]             = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSector, setFilterSector] = useState("all");
  const [viewMode, setViewMode]         = useState("table");
  const [modal, setModal]               = useState(null);
  const [selected, setSelected]         = useState(null);
  const [form, setForm]                 = useState({ ...EMPTY_FORM });
  const [saving, setSaving]             = useState(false);

  useEffect(() => { fetchEvents(); fetchSectors(); }, []);

  async function fetchSectors() {
    try {
      const res = await api.get("/sectors");
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.sectors ?? []);
      const sorted = [...data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSectors(sorted);
    } catch (e) {
      console.error("Error fetching sectors:", e);
    }
  }

  async function fetchEvents() {
    setLoading(true); setError("");
    try {
      let res;
      try {
        res = await api.get("/events/admin/all");
      } catch (firstErr) {
        if (firstErr.response?.status === 404) {
          res = await api.get("/events");
        } else {
          throw firstErr;
        }
      }
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.events ?? []);
      setEvents(data);
    } catch (e) {
      const status = e.response?.status;
      const msg    = e.response?.data?.message || e.message || t.unknownError;
      setError(`${t.errLoad} (${status ? `HTTP ${status}: ` : ""}${msg})`);
    } finally {
      setLoading(false);
    }
  }

  const STATUS_TO_BACKEND = {
    "चलिरहेको": "current", "current": "current", "Ongoing": "current",
    "आगामी": "upcoming",   "upcoming": "upcoming", "Upcoming": "upcoming",
    "सम्पन्न": "past",     "past": "past", "Past": "past",
  };
  function statusKey(s) { return STATUS_TO_BACKEND[s] || s; }
  function statusLabel(s) { return statusLabels[statusKey(s)] || s; }

  const statusColors = {
    current:  { bg: O2,                       color: O,         border: O         },
    upcoming: { bg: G3,                       color: G,         border: G         },
    past:     { bg: "rgba(99,102,241,0.10)",  color: "#6366f1", border: "#6366f1" },
  };

  const catBadge = () => ({
    display: "inline-block", padding: "2px 10px", borderRadius: 20,
    fontSize: 10, fontWeight: 600, whiteSpace: "nowrap",
    background: O2, color: O, border: `1px solid ${O}`,
    maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis",
    boxSizing: "border-box", verticalAlign: "middle",
  });

  const statusBadge = (s) => {
    const c = statusColors[statusKey(s)] || statusColors.upcoming;
    return {
      display: "inline-block", padding: "2px 10px", borderRadius: 20,
      fontSize: 10, fontWeight: 600, whiteSpace: "nowrap",
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    };
  };

  function displayCategory(c) {
    
    return c || "";
  }

  const filtered = events.filter((e) => {
    const q = search.toLowerCase();
    const matchSearch = e.title?.toLowerCase().includes(q) || e.organizer?.toLowerCase().includes(q);
    const matchStatus = filterStatus === "all" || statusKey(e.status) === filterStatus;
    const matchSector = filterSector === "all" || e.category === filterSector;
    return matchSearch && matchStatus && matchSector;
  });

  const evStats = [
    { label: t.statTotal,    value: events.length,                                                icon: "📅", delta: t.deltaSoFar },
    { label: t.statOngoing,  value: events.filter(e => statusKey(e.status) === "current").length,  icon: "🟠", delta: t.deltaNow },
    { label: t.statUpcoming, value: events.filter(e => statusKey(e.status) === "upcoming").length, icon: "🔜", delta: t.deltaSchedule },
    { label: t.statPast,     value: events.filter(e => statusKey(e.status) === "past").length,     icon: "✅", delta: t.deltaDone },
  ];

  function openAdd()       { setForm({ ...EMPTY_FORM }); setSelected(null); setModal("add"); }
  function openEdit(ev)    { setForm({ title: ev.title, description: ev.description || "", date: ev.date, organizer: ev.organizer, category: ev.category || sectors[0]?.name || "", attendees: ev.attendees, status: statusKey(ev.status), featured: ev.featured, imageUrl: ev.imageUrl || "", gallery: ev.gallery || [] }); setSelected(ev); setModal("edit"); }
  function openDelete(ev)  { setSelected(ev); setModal("delete"); }
  function closeModal()    { setModal(null); setSelected(null); setSaving(false); }

  async function handleSave() {
    if (!form.title.trim() || !form.date || !form.organizer.trim() || !form.description?.trim() || !form.category) return;
    setSaving(true);
    try {
      const imageUrl = await uploadImageFile(form.imageUrl);
      const gallery  = await uploadImageFiles(form.gallery);
      const payload  = { ...form, imageUrl, gallery, attendees: Number(form.attendees) || 0, status: statusKey(form.status) };
      if (modal === "add") {
        const res     = await api.post("/events", payload);
        const created = res.data?.data ?? res.data;
        setEvents(prev => [created, ...prev]);
      } else {
        const res     = await api.put(`/events/${selected._id}`, payload);
        const updated = res.data?.data ?? res.data;
        setEvents(prev => prev.map(e => e._id === selected._id ? updated : e));
      }
      closeModal();
    } catch (e) {
      alert(e.response?.data?.message || t.errSave);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await api.delete(`/events/${selected._id}`);
      setEvents(prev => prev.filter(e => e._id !== selected._id));
      closeModal();
    } catch (e) {
      alert(e.response?.data?.message || t.errDelete);
    } finally {
      setSaving(false);
    }
  }

  async function toggleFeatured(ev) {
    try {
      const res     = await api.patch(`/events/${ev._id}`, { featured: !ev.featured });
      const updated = res.data?.data ?? res.data;
      setEvents(prev => prev.map(e => e._id === ev._id ? { ...e, featured: updated.featured } : e));
    } catch (e) {
      alert(e.response?.data?.message || t.errUpdate);
    }
  }

  const inp = { width: "100%", padding: "7px 10px", borderRadius: 7, border: "1.5px solid #e5e7eb", fontSize: 13, color: "#111827", outline: "none", boxSizing: "border-box", background: "#fff" };
  const lbl = { fontSize: 10, color: "#9ca3af", fontWeight: 600, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.05em" };
  const EVENTS_COLS = "minmax(0,1.3fr) minmax(0,1.7fr) minmax(0,0.85fr) minmax(0,1.1fr) minmax(0,0.6fr) minmax(0,0.85fr) minmax(0,1.5fr)";

  // Splits a comma-separated organizer string into individual trimmed names.
  function organizerList(organizer) {
    return (organizer || "").split(",").map(o => o.trim()).filter(Boolean);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 10 }}>
        {evStats.map((s) => (
          <div key={s.label} style={S.statCard}>
            <div style={S.statIcon}>{s.icon}</div>
            <div>
              <div style={S.statVal}>{s.value}</div>
              <div style={S.statLabel}>{s.label}</div>
              <div style={S.statDelta}>{s.delta}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...S.card, padding: "12px 16px" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input placeholder={t.searchPlaceholder} value={search} onChange={e => setSearch(e.target.value)} style={{ ...inp, flex: "1 1 180px", width: "auto", minWidth: 0, maxWidth: isMobile ? "100%" : 220, border: "1.5px solid #e5e7eb" }} />
          <select value={filterSector} onChange={e => setFilterSector(e.target.value)} style={{ ...inp, width: "auto", minWidth: 140 }}>
            <option value="all">{t.filterAllSector}</option>
            {sectors.map((s) => (
              <option key={s._id} value={s.name}>{s.name}</option>
            ))}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inp, width: "auto", minWidth: 110 }}>
            <option value="all">{t.filterAllStatus}</option>
            {STATUS_KEYS.map(k => <option key={k} value={k}>{statusLabels[k]}</option>)}
          </select>
          <div style={{ display: "flex", border: `1px solid ${G}`, borderRadius: 7, overflow: "hidden", marginLeft: isMobile ? 0 : "auto" }}>
            {["table", "grid"].map(v => (
              <button key={v} onClick={() => setViewMode(v)}
                style={{ padding: "6px 14px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, background: viewMode === v ? G : "#fff", color: viewMode === v ? "#fff" : G }}>
                {v === "table" ? t.viewTable : t.viewGrid}
              </button>
            ))}
          </div>
          <button onClick={fetchEvents} style={{ padding: "7px 14px", borderRadius: 7, background: G3, border: `1px solid ${G}`, color: G, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{t.refresh}</button>
          <button onClick={openAdd} style={{ padding: "7px 16px", borderRadius: 7, background: G, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{t.addBtn}</button>
        </div>
      </div>

      {error && <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 16px", color: "#ef4444", fontSize: 13 }}>⚠️ {error}</div>}

      {viewMode === "table" && (
        <div style={{ ...S.tableWrap, ...S.tableScroll }}>
          <div style={{ minWidth: isMobile ? 760 : undefined }}>
            <div style={{ display: "grid", gridTemplateColumns: EVENTS_COLS, gap: 14, padding: "8px 16px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: 10, color: "#9ca3af", fontWeight: 700, letterSpacing: "0.06em" }}>
              <span>{t.colTitle}</span><span>{t.colSector}</span><span>{t.colDate}</span><span>{t.colOrganizer}</span><span>{t.colAttendees}</span><span>{t.colStatus}</span><span>{t.colActions}</span>
            </div>
            {loading && <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.loading}</div>}
            {!loading && filtered.length === 0 && <div style={{ padding: "24px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.empty}</div>}
            {!loading && filtered.map((ev, i) => (
              <div key={ev._id} style={{ display: "grid", gridTemplateColumns: EVENTS_COLS, gap: 14, padding: "10px 16px", alignItems: "center", borderBottom: i === filtered.length - 1 ? "none" : "1px solid #f3f4f6", fontSize: 12, color: "#374151" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, color: "#111827", minWidth: 0, paddingRight: 12 }}>
                  {ev.featured && <span title={t.featuredTitle} style={{ color: O, fontSize: 13, flexShrink: 0 }}>⭐</span>}
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{ev.title}</span>
                </span>
                <div style={{ display: "flex", alignItems: "center", minWidth: 0, overflow: "hidden", paddingRight: 12 }} title={displayCategory(ev.category)}>
                  <span style={catBadge()}>{displayCategory(ev.category)}</span>
                </div>
                <span style={{ color: "#6b7280", fontSize: 11 }}>{ev.date ? new Date(ev.date).toLocaleDateString(t.locale, { year:"numeric", month:"short", day:"numeric" }) : "—"}</span>
                <span style={{ display: "flex", flexWrap: "wrap", gap: 3, minWidth: 0 }}>
                  {organizerList(ev.organizer).map((name, idx) => (
                    <span key={idx} style={{ background: "#f3f4f6", color: "#4b5563", borderRadius: 10, padding: "1px 7px", fontSize: 10, whiteSpace: "nowrap" }}>{name}</span>
                  ))}
                </span>
                <span style={{ color: "#374151", fontWeight: 600, textAlign: "center" }}>{ev.attendees}</span>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={statusBadge(ev.status)}>{statusLabel(ev.status)}</span>
                </div>
                <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <button onClick={() => toggleFeatured(ev)} title={ev.featured ? t.unfeatureTitle : t.featuredTitle}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: ev.featured ? O : "#d1d5db", padding: 0, flexShrink: 0 }}>⭐</button>
                  <button onClick={() => openEdit(ev)}
                    style={{ background: G3, border: `1px solid ${G}`, color: G, borderRadius: 5, padding: "3px 6px", fontSize: 10, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>{t.edit}</button>
                  <button onClick={() => openDelete(ev)}
                    style={{ background: "rgba(239,68,68,0.1)", border: "1px solid #ef4444", color: "#ef4444", borderRadius: 5, padding: "3px 6px", fontSize: 10, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>{t.delete}</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {viewMode === "grid" && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 16 }}>
          {loading && <div style={{ gridColumn: "1/-1", padding: "30px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.loading}</div>}
          {!loading && filtered.length === 0 && <div style={{ gridColumn: "1/-1", padding: "24px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.empty}</div>}
          {!loading && filtered.map((ev) => {
            const organizers = organizerList(ev.organizer);
            return (
            <div key={ev._id} style={{ ...S.card, display: "flex", flexDirection: "column", gap: 12, position: "relative", padding: 16 }}>
              {ev.featured && <span style={{ position: "absolute", top: 14, right: 14, color: O, fontSize: 15 }}>⭐</span>}

              <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.35, paddingRight: 22, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                {ev.title}
              </div>

              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {ev.category && <span style={catBadge()}>{displayCategory(ev.category)}</span>}
                <span style={statusBadge(ev.status)}>{statusLabel(ev.status)}</span>
              </div>

              <div style={{ fontSize: 11.5, color: "#6b7280", display: "flex", flexDirection: "column", gap: 7, flex: 1 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  📅 <span>{ev.date ? new Date(ev.date).toLocaleDateString(t.locale, { year: "numeric", month: "short", day: "numeric" }) : "—"}</span>
                </span>

                {organizers.length > 0 && (
                  <span style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                    <span style={{ flexShrink: 0 }}>👤</span>
                    {organizers.map((name, idx) => (
                      <span key={idx} style={{ background: "#f3f4f6", color: "#4b5563", borderRadius: 10, padding: "2px 8px", fontSize: 10, whiteSpace: "nowrap" }}>{name}</span>
                    ))}
                  </span>
                )}

                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  👥 <span>{ev.attendees} {language === "en" ? "attendees" : "सहभागी"}</span>
                </span>
              </div>

              <div style={{ display: "flex", gap: 6, alignItems: "center", paddingTop: 10, borderTop: "1px solid #f3f4f6" }}>
                <button onClick={() => toggleFeatured(ev)}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, color: ev.featured ? O : "#d1d5db", padding: 0, flexShrink: 0 }}>⭐</button>
                <button onClick={() => openEdit(ev)}
                  style={{ flex: 1, background: G3, border: `1px solid ${G}`, color: G, borderRadius: 6, padding: "6px 0", fontSize: 11, cursor: "pointer", fontWeight: 600 }}>✏️ {t.edit}</button>
                <button onClick={() => openDelete(ev)}
                  style={{ flex: 1, background: "rgba(239,68,68,0.1)", border: "1px solid #ef4444", color: "#ef4444", borderRadius: 6, padding: "6px 0", fontSize: 11, cursor: "pointer", fontWeight: 600 }}>🗑️ {t.delete}</button>
              </div>
            </div>
          );})}
        </div>
      )}

      {(modal === "add" || modal === "edit") && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={closeModal}>
          <div style={{ background: "#fff", borderRadius: 14, width: 580, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", animation: "slideUp 0.22s ease" }} onClick={e => e.stopPropagation()}>
            <div style={{ background: `linear-gradient(135deg, ${G} 0%, #0a5c2e 100%)`, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "#fff", fontSize: 15, fontWeight: 700 }}>{modal === "add" ? t.modalAdd : t.modalEdit}</span>
              <button onClick={closeModal} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 26, height: 26, borderRadius: "50%", cursor: "pointer", fontSize: 14 }}>×</button>
            </div>
            <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 13 }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={lbl}>{t.lblTitle}</div>
                  <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder={t.titlePlaceholder} style={inp} />
                </div>
                <div>
                  <div style={lbl}>{t.lblDate}</div>
                  <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} style={inp} />
                </div>
                <div>
                  <div style={lbl}>{t.lblOrganizer}</div>
                  <input value={form.organizer} onChange={e => setForm({ ...form, organizer: e.target.value })} placeholder={t.organizerPlaceholder} style={inp} />
                </div>
                <div>
                  <div style={lbl}>{t.lblAttendees}</div>
                  <input type="number" value={form.attendees} onChange={e => setForm({ ...form, attendees: e.target.value })} placeholder="0" style={inp} />
                </div>
                <div>
                  <div style={lbl}>{t.lblCategory}</div>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={inp}>
                    <option value="" disabled>{language === "en" ? "Select a sector" : "क्षेत्र छान्नुहोस्"}</option>
                    {sectors.map((s) => (
                      <option key={s._id} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={lbl}>{t.lblStatus}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {STATUS_KEYS.map(k => (
                      <button key={k} onClick={() => setForm({ ...form, status: k })}
                        style={{ flex: 1, padding: "6px 4px", borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: "pointer", border: `1.5px solid ${statusKey(form.status) === k ? G : "#e5e7eb"}`, background: statusKey(form.status) === k ? G3 : "#fff", color: statusKey(form.status) === k ? G : "#6b7280" }}>
                        {statusLabels[k]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <div style={lbl}>{t.lblDescription}</div>
                <div className="admin-quill-wrap">
                  <ReactQuill
                    theme="snow"
                    value={form.description}
                    onChange={val => setForm({ ...form, description: val })}
                    placeholder={t.descPlaceholder}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                  />
                </div>
              </div>
              <div>
                <div style={lbl}>{t.lblMainImage}</div>
                <ImageUploadField
                  value={form.imageUrl}
                  onChange={url => setForm({ ...form, imageUrl: url })}
                />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={lbl}>{t.lblGallery(form.gallery.length)}</div>
                  <GalleryUploadBtn
                    onUpload={url => setForm(f => ({ ...f, gallery: [...f.gallery, url] }))}
                  />
                </div>
                {form.gallery.length === 0 ? (
                  <div style={{ padding: "14px", background: "#f9fafb", borderRadius: 8, border: "1.5px dashed #e5e7eb", textAlign: "center", color: "#9ca3af", fontSize: 12 }}>
                    {t.galleryEmpty}
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 8 }}>
                    {form.gallery.map((url, idx) => (
                      <div key={idx} style={{ position: "relative" }}>
                        <img src={getImageUrl(url)} alt={`gallery-${idx}`}
                          style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 7, border: "1.5px solid #e5e7eb", display: "block" }}
                          onError={e => e.target.style.opacity = "0.3"}
                        />
                        <button
                          onClick={() => setForm(f => ({ ...f, gallery: f.gallery.filter((_, i) => i !== idx) }))}
                          style={{ position: "absolute", top: 3, right: 3, background: "rgba(0,0,0,0.6)", border: "none", color: "#fff", width: 20, height: 20, borderRadius: "50%", cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={lbl}>{t.lblFeatured}</div>
                <div onClick={() => setForm({ ...form, featured: !form.featured })}
                  style={{ width: 36, height: 20, borderRadius: 10, background: form.featured ? G : "#d1d5db", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                  <div style={{ position: "absolute", top: 2, left: form.featured ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }} />
                </div>
                <span style={{ fontSize: 11, color: "#6b7280" }}>{form.featured ? t.featuredYes : t.featuredNo}</span>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <button onClick={handleSave} disabled={saving}
                  style={{ flex: 1, padding: "9px", borderRadius: 8, background: G, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
                  {saving ? t.savingBtn : (modal === "add" ? t.saveBtnAdd : t.saveBtnEdit)}
                </button>
                <button onClick={closeModal}
                  style={{ flex: 1, padding: "9px", borderRadius: 8, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  {t.cancel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modal === "delete" && selected && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={closeModal}>
          <div style={{ background: "#fff", borderRadius: 14, width: 380, maxWidth: "95vw", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", animation: "slideUp 0.22s ease", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
            <div style={{ background: "rgba(239,68,68,0.08)", borderBottom: "1px solid #fee2e2", padding: "16px 20px", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>🗑️</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#991b1b" }}>{t.deleteTitle}</span>
            </div>
            <div style={{ padding: "18px 20px" }}>
              <p style={{ fontSize: 13, color: "#374151", margin: 0 }}>
                {t.deleteConfirm(selected.title)}
              </p>
              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <button onClick={handleDelete} disabled={saving}
                  style={{ flex: 1, padding: "9px", borderRadius: 8, background: "#ef4444", color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
                  {saving ? t.deleting : t.deleteYes}
                </button>
                <button onClick={closeModal}
                  style={{ flex: 1, padding: "9px", borderRadius: 8, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  {t.cancel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
        .admin-quill-wrap .ql-toolbar { border-radius: 7px 7px 0 0; border-color: #e5e7eb; background: #f9fafb; }
        .admin-quill-wrap .ql-container { border-radius: 0 0 7px 7px; border-color: #e5e7eb; font-size: 13px; min-height: 100px; }
        .admin-quill-wrap .ql-editor { min-height: 100px; }
      `}</style>
    </div>
  );
}

export default EventsPage;
